import React from 'react';
import BlockIcon from '@material-ui/icons/Block';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { HugeiconsIcon } from '@hugeicons/react';
import { Link01Icon } from '@hugeicons/core-free-icons';
import { PromoCodeRow } from '../types';
import { AppliedToBadge } from './AppliedToBadge';
import { FilterButton } from './FilterButton';
import { PromoLinkModal } from './PromoLinkModal';
import Modal from '../../shared/components/core-ui/modal/modal.component';
import { hasRole } from '../../shared/services/helpers/auth-helper/auth-helper.service';
import { UserRole } from '../../shared/enums/user-role.enum';

type StatusFilter = 'all' | 'active' | 'inactive';

export function PromoCodesTable({
  promoCodeRows,
  isPromotionsLoading,
  onStatusChange,
  onAutomaticChange
}: {
  promoCodeRows: PromoCodeRow[];
  isPromotionsLoading: boolean;
  onStatusChange: (promotionId: string, isActive: boolean) => Promise<boolean>;
  onAutomaticChange: (promotionId: string, isAutomatic: boolean) => Promise<boolean>;
}) {
  const [openActionMenu, setOpenActionMenu] = React.useState<string | null>(null);
  const [dateRangesPromoCode, setDateRangesPromoCode] = React.useState<PromoCodeRow | null>(null);
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [updatingPromotionId, setUpdatingPromotionId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState('');
  const [linkPromoCode, setLinkPromoCode] = React.useState<string | null>(null);

  const visiblePromoCodeRows = promoCodeRows.filter((promoCode) => {
    if (statusFilter === 'active') return promoCode.isActive;
    if (statusFilter === 'inactive') return !promoCode.isActive;

    return true;
  });
  const emptyStateMessage =
    statusFilter === 'active'
      ? 'No active promo codes found'
      : statusFilter === 'inactive'
      ? 'No deactivated promo codes found'
      : 'No promo codes found';

  React.useEffect(() => {
    if (!openActionMenu) return;

    function handleDocumentMouseDown(event: MouseEvent) {
      const target = event.target;

      if (!(target instanceof Element)) return;

      if (target.closest('[data-promo-action-trigger], [data-promo-action-menu]')) return;

      setOpenActionMenu(null);
    }

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [openActionMenu]);

  function handleStatusFilterChange(nextStatusFilter: StatusFilter) {
    setStatusFilter(nextStatusFilter);
    setOpenActionMenu(null);
  }

  async function handleStatusChange(promoCode: PromoCodeRow) {
    setActionError('');
    setUpdatingPromotionId(promoCode.id);

    const wasUpdated = await onStatusChange(promoCode.id, !promoCode.isActive);

    setUpdatingPromotionId(null);
    setOpenActionMenu(null);

    if (!wasUpdated) {
      setActionError(
        `Could not ${promoCode.isActive ? 'deactivate' : 'activate'} ${
          promoCode.code
        }. Please try again.`
      );
    }
  }

  async function handleAutomaticChange(promoCode: PromoCodeRow) {
    setActionError('');
    setUpdatingPromotionId(promoCode.id);

    const wasUpdated = await onAutomaticChange(promoCode.id, !promoCode.isAutomatic);

    setUpdatingPromotionId(null);
    setOpenActionMenu(null);

    if (!wasUpdated) {
      setActionError(
        `Could not ${promoCode.isAutomatic ? 'unmark' : 'mark'} ${
          promoCode.code
        } as automatic. Please try again.`
      );
    }
  }

  return (
    <section className="min-h-[calc(100vh-190px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-end border-b border-gray-200 px-6 py-5">
        <div className="flex flex-wrap items-center justify-end gap-4">
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => handleStatusFilterChange('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === 'active'}
            onClick={() => handleStatusFilterChange('active')}
          >
            Active
          </FilterButton>
          <FilterButton
            active={statusFilter === 'inactive'}
            onClick={() => handleStatusFilterChange('inactive')}
          >
            Deactivated
          </FilterButton>
        </div>
      </div>

      {actionError ? (
        <div className="border-b border-red-100 bg-red-50 px-6 py-4 text-lg font-gilroy-medium text-red-600">
          {actionError}
        </div>
      ) : null}

      <div className="overflow-x-auto pb-40">
        <table className="w-full min-w-[1600px] border-separate border-spacing-0 text-left">
          <thead>
            <tr className="border-b border-gray-200 text-xl font-gilroy-bold text-neutral-700">
              <th className="border-b border-gray-200 px-10 py-7 font-semibold">Promo code</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Discount value</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Creation date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Start date</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">End date</th>
              <th className="border-b border-gray-200 px-6 py-7 text-center font-semibold">
                Date ranges
              </th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Applied to</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Max uses / user</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Number of used</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Status</th>
              <th className="border-b border-gray-200 px-6 py-7 font-semibold">Automatic</th>
              <th className="border-b border-gray-200 px-10 py-7 text-center font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isPromotionsLoading ? (
              <tr>
                <td
                  className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                  colSpan={12}
                >
                  Loading promo codes...
                </td>
              </tr>
            ) : null}
            {!isPromotionsLoading && !visiblePromoCodeRows.length ? (
              <tr>
                <td
                  className="px-10 py-12 text-center text-xl font-gilroy-medium text-neutral-400"
                  colSpan={12}
                >
                  {emptyStateMessage}
                </td>
              </tr>
            ) : null}
            {!isPromotionsLoading &&
              visiblePromoCodeRows.map((promoCode) => (
                <tr key={promoCode.id} className="text-2xl font-gilroy-medium text-neutral-700">
                  <td className="whitespace-nowrap px-10 py-9 align-top">{promoCode.code}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">
                    {promoCode.discountValue}
                  </td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.createdAt}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.startDate}</td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">{promoCode.endDate}</td>
                  <td className="px-6 py-7 text-center align-top">
                    {promoCode.dateRanges.length ? (
                      <button
                        type="button"
                        aria-label={`View date ranges for ${promoCode.code}`}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 text-base font-gilroy-bold text-blue-700 transition hover:border-blue-200 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        onClick={() => setDateRangesPromoCode(promoCode)}
                      >
                        <CalendarTodayOutlinedIcon className="!h-6 !w-6" />
                        <span>{promoCode.dateRanges.length}</span>
                      </button>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-8 align-top">
                    <div className="flex flex-wrap items-center gap-3">
                      {(promoCode.appliedTo.length ? promoCode.appliedTo : ['All']).map((item) => (
                        <AppliedToBadge key={item}>{item}</AppliedToBadge>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">
                    {promoCode.maximumUsesPerUser ?? '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-9 align-top">
                    {promoCode.numberOfUsed ?? '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-8 align-top">
                    <span
                      className={`inline-flex items-center rounded-xl px-6 py-3 text-lg font-gilroy-bold leading-none shadow-sm ring-1 ${
                        promoCode.isActive
                          ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                          : 'bg-rose-50 text-rose-600 ring-rose-100'
                      }`}
                    >
                      {promoCode.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-8 align-top">
                    <span
                      className={`inline-flex items-center rounded-xl px-6 py-3 text-lg font-gilroy-bold leading-none shadow-sm ring-1 ${
                        promoCode.isAutomatic
                          ? 'bg-sky-50 text-sky-700 ring-sky-100'
                          : 'bg-gray-50 text-neutral-500 ring-gray-100'
                      }`}
                    >
                      {promoCode.isAutomatic ? 'Automatic' : 'Manual'}
                    </span>
                  </td>
                  <td className="relative px-10 py-7 text-center align-top">
                    {hasRole([
                      UserRole.SUPER_ADMIN,
                      UserRole.ADMIN,
                      UserRole.ECOMMERCE_SENIOR_SPECIALIST
                    ]) ? (
                      <button
                        data-promo-action-trigger
                        type="button"
                        aria-label={`Open actions for ${promoCode.code}`}
                        aria-expanded={openActionMenu === promoCode.id}
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full text-neutral-900 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        onClick={() =>
                          setOpenActionMenu((currentOpenMenu) =>
                            currentOpenMenu === promoCode.id ? null : promoCode.id
                          )
                        }
                      >
                        <MoreVertIcon className="!h-7 !w-7" />
                      </button>
                    ) : (
                      <MoreVertIcon className="!h-7 !w-7 text-neutral-100" />
                    )}

                    {openActionMenu === promoCode.id ? (
                      <div
                        data-promo-action-menu
                        className="absolute right-7 top-20 z-30 w-[260px] rounded-xl border border-gray-100 bg-white p-2 text-left shadow-[0_12px_28px_rgba(15,23,42,0.12)]"
                      >
                        <button
                          type="button"
                          disabled={updatingPromotionId === promoCode.id}
                          className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-lg font-gilroy-bold transition focus:outline-none focus-visible:ring-2 disabled:cursor-wait disabled:opacity-60 ${
                            promoCode.isActive
                              ? 'text-red-500 hover:bg-red-50 hover:text-red-600 focus-visible:bg-red-50 focus-visible:ring-red-100'
                              : 'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 focus-visible:bg-emerald-50 focus-visible:ring-emerald-100'
                          }`}
                          onClick={() => handleStatusChange(promoCode)}
                        >
                          {promoCode.isActive ? (
                            <BlockIcon className="!h-6 !w-6" />
                          ) : (
                            <CheckCircleOutlineIcon className="!h-6 !w-6" />
                          )}
                          <span>
                            {updatingPromotionId === promoCode.id
                              ? 'Updating...'
                              : promoCode.isActive
                              ? 'Deactivate promo code'
                              : 'Activate promo code'}
                          </span>
                        </button>
                        <button
                          type="button"
                          disabled={updatingPromotionId === promoCode.id}
                          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-lg font-gilroy-bold text-neutral-700 transition hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-200 disabled:cursor-wait disabled:opacity-60"
                          onClick={() => handleAutomaticChange(promoCode)}
                        >
                          <CheckCircleOutlineIcon className="!h-6 !w-6" />
                          <span>
                            {updatingPromotionId === promoCode.id
                              ? 'Updating...'
                              : promoCode.isAutomatic
                              ? 'Unmark as automatic'
                              : 'Mark as automatic'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-lg font-gilroy-bold text-neutral-700 transition hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-200"
                          onClick={() => {
                            setOpenActionMenu(null);
                            setLinkPromoCode(promoCode.code);
                          }}
                        >
                          <HugeiconsIcon icon={Link01Icon} size={24} />
                          <span>Copy link</span>
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {linkPromoCode ? (
        <PromoLinkModal code={linkPromoCode} onClose={() => setLinkPromoCode(null)} />
      ) : null}

      {dateRangesPromoCode ? (
        <Modal
          config={{
            showModal: true,
            fullScreen: false,
            showClose: false,
            maxWidth: 'xs'
          }}
          eventHandlers={{
            closeModalHandler: () => setDateRangesPromoCode(null)
          }}
        >
          <div className="w-full rounded-2xl bg-white p-7 md:p-8">
            <div className="mb-6 flex items-start justify-between gap-5">
              <div>
                <h2 className="text-2xl font-gilroy-bold text-neutral-900">Date ranges</h2>
                <p className="mt-1 text-base font-gilroy-medium text-neutral-500">
                  {dateRangesPromoCode.code}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close date ranges"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-neutral-600 transition hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                onClick={() => setDateRangesPromoCode(null)}
              >
                <CloseIcon className="!h-7 !w-7" />
              </button>
            </div>

            <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
              {dateRangesPromoCode.dateRanges.map((dateRange, index) => (
                <div
                  key={`${dateRange.from}-${dateRange.to}-${index}`}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                >
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <div>
                      <span className="block text-sm font-gilroy-medium text-neutral-400">
                        From
                      </span>
                      <span className="mt-1 block whitespace-nowrap text-lg font-gilroy-bold text-neutral-800">
                        {dateRange.from}
                      </span>
                    </div>
                    <div>
                      <span className="block text-sm font-gilroy-medium text-neutral-400">To</span>
                      <span className="mt-1 block whitespace-nowrap text-lg font-gilroy-bold text-neutral-800">
                        {dateRange.to}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
